import { useState, useMemo } from 'react'
import { useQuery, useQueryClient, useMutation } from 'react-query'
import { useAuth } from '../hooks/useAuth'
import Card, { CardHeader, CardTitle, CardContent } from '../components/UI/Card'
import Button from '../components/UI/Button'
import Input from '../components/UI/Input'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import AddRoleModal from '../components/Modals/AddRoleModal'
import { rolesAPI } from '../services/api'
import { productsAPI } from '../services/productsAPI'
import { Shield, Plus, Search, Edit, Trash2, Key, Users, X, ToggleLeft, ToggleRight } from 'lucide-react'
import ConfirmationModal from '../components/Modals/ConfirmationModal'
import { cn } from '../utils/cn'
import toast from 'react-hot-toast'
 

export default function Roles() {
  const { user, hasPermission, isControlCenter } = useAuth()
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [userType, setUserType] = useState('control_center')
  const [selectedRole, setSelectedRole] = useState(null)
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false)
  const [isAddRoleModalOpen, setIsAddRoleModalOpen] = useState(false)
  const [editingRole, setEditingRole] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [roleToDelete, setRoleToDelete] = useState(null)
  const [expandedPaths, setExpandedPaths] = useState(new Set())
  const [rolePermissionsDraft, setRolePermissionsDraft] = useState({})
 
  // Fetch only hub roles (control_center) from product_roles table - optimized caching
  const { data: rolesData, isLoading, refetch } = useQuery(
    ['roles'],
    () => rolesAPI.getAll({ user_type: 'control_center' }),
    { 
      enabled: isControlCenter(),
      staleTime: 10 * 60 * 1000, // 10 minutes (roles don't change often)
      cacheTime: 30 * 60 * 1000,
      refetchOnMount: false,
    }
  )

  // Fetch permissions from hub permissions endpoint - optimized caching
  const { data: hubPermissionsData, isLoading: permissionsLoading } = useQuery(
    'hubPermissions',
    () => productsAPI.getHubPermissions(),
    { 
      enabled: isControlCenter(),
      staleTime: 30 * 60 * 1000, // 30 minutes (permissions rarely change)
      cacheTime: 60 * 60 * 1000, // 1 hour
      refetchOnMount: false,
    }
  )

  const allRoles = rolesData?.data?.data?.roles || []

  // Filter roles by search term (name and description)
  const roles = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase()
    if (!term) return allRoles
    return allRoles.filter(
      (role) =>
        (role.name && role.name.toLowerCase().includes(term)) ||
        (role.description && role.description.toLowerCase().includes(term))
    )
  }, [allRoles, searchTerm])
  
  // Convert flat permissions array to nested tree structure
  const permissions = (() => {
    const hubPermissions = hubPermissionsData?.data?.data?.permissions || []
    
    // Convert flat permissions to nested structure
    const nestedPermissions = {}
    
    hubPermissions.forEach(permission => {
      if (permission.permissions && typeof permission.permissions === 'object') {
        // Extract permission key (e.g., "organizations.create")
        const permissionKey = Object.keys(permission.permissions).find(key => permission.permissions[key] === true)
        
        if (permissionKey) {
          // Split permission key into parts (e.g., "organizations.create" -> ["organizations", "create"])
          const parts = permissionKey.split('.')
          
          if (parts.length === 2) {
            const [resource, action] = parts
            
            // Initialize resource if not exists
            if (!nestedPermissions[resource]) {
              nestedPermissions[resource] = {}
            }
            
            // Add action to resource
            nestedPermissions[resource][action] = true
          }
        }
      }
    })
    
    return Object.keys(nestedPermissions).length > 0 ? nestedPermissions : {
      users: { create: true, read: true, update: true, delete: true },
      organizations: { create: true, read: true, update: true, delete: true },
      roles: { create: true, read: true, update: true, delete: true },
      products: { create: true, read: true, update: true, delete: true },
      analytics: { read: true },
      settings: { update: true },
      system: { admin: true }
    }
  })()
  
  console.log('Hub permissions data:', hubPermissionsData)
  console.log('Converted permissions tree:', permissions)
 
  const handleSearch = (e) => {
    setSearchTerm(e.target.value)
  }
 
  const handleUserTypeChange = (e) => {
    setUserType(e.target.value)
  }
 
  const getPermissionCount = (role) => {
    if (typeof role.permissions === 'string') {
      role.permissions = JSON.parse(role.permissions)
    }
    return Object.keys(role.permissions || {}).filter(key => role.permissions[key]).length
  }

  const getPermissionList = (role) => {
    if (typeof role.permissions === 'string') {
      role.permissions = JSON.parse(role.permissions)
    }
    const permissions = role.permissions || {}
    const permissionEntries = Object.entries(permissions)
      .map(([key, value]) => `${key}: ${value ? 'Enable' : 'Disable'}`)
    return permissionEntries.length > 0 ? permissionEntries.join(', ') : 'No permissions'
  }

  // Create role mutation
  const createRoleMutation = useMutation(
    (roleData) => rolesAPI.create(roleData),
    {
      onSuccess: () => {
        toast.success('Role created successfully!')
        queryClient.invalidateQueries(['roles'])
        setIsAddRoleModalOpen(false)
        setEditingRole(null)
      },
      onError: (error) => {
        console.error('Error creating role:', error)
        toast.error(error.response?.data?.message || 'Failed to create role')
      }
    }
  )

  // Update role mutation
  const updateRoleMutation = useMutation(
    ({ roleId, roleData }) => rolesAPI.update(roleId, roleData),
    {
      onSuccess: () => {
        toast.success('Role updated successfully!')
        queryClient.invalidateQueries(['roles'])
        setIsAddRoleModalOpen(false)
        setEditingRole(null)
      },
      onError: (error) => {
        console.error('Error updating role:', error)
        toast.error(error.response?.data?.message || 'Failed to update role')
      }
    }
  )

  // Delete role mutation
  const deleteRoleMutation = useMutation(
    (roleId) => rolesAPI.delete(roleId),
    {
      onSuccess: () => {
        toast.success('Role deleted successfully!')
        queryClient.invalidateQueries(['roles'])
        setShowDeleteModal(false)
        setRoleToDelete(null)
      },
      onError: (error) => {
        console.error('Error deleting role:', error)
        toast.error(error.response?.data?.message || 'Failed to delete role')
      }
    }
  )

  // Toggle role status mutation
  const toggleRoleStatusMutation = useMutation(
    ({ roleId, status }) => rolesAPI.update(roleId, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['roles'])
      },
      onError: (error) => {
        console.error('Error toggling role status:', error)
        toast.error('Failed to update role status')
      }
    }
  )

  const openAddRoleModal = () => {
    setEditingRole(null)
    setIsAddRoleModalOpen(true)
  }

  const openEditRoleModal = (role) => {
    setEditingRole(role)
    setIsAddRoleModalOpen(true)
  }

  const closeAddRoleModal = () => {
    setIsAddRoleModalOpen(false)
    setEditingRole(null)
  }

  const handleRoleSubmit = async (roleData) => {
    setIsSubmitting(true)
    try {
      if (editingRole) {
        await updateRoleMutation.mutateAsync({
          roleId: editingRole.id,
          roleData: {
            ...roleData
            // user_type will be defaulted to 'control_center' in the backend if not provided
          }
        })
      } else {
        await createRoleMutation.mutateAsync({
          ...roleData
          // user_type will be defaulted to 'control_center' in the backend if not provided
        })
      }
    } catch (error) {
      // Error handled in mutation onError
    } finally {
      setIsSubmitting(false)
    }
  }
 
  const getUserTypeBadge = (userType) => {
    const config = {
      control_center: { color: 'bg-purple-100 text-purple-800', label: 'Control Center' },
      organization: { color: 'bg-blue-100 text-blue-800', label: 'Organization' }
    }
   
    const typeConfig = config[userType] || config.organization
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeConfig.color}`}>
        {typeConfig.label}
      </span>
    )
  }
 
  const getPermissionBadges = (role) => {
    if (typeof role.permissions === 'string') {
      role.permissions = JSON.parse(role.permissions)
    }
   
    const activePermissions = Object.keys(role.permissions).filter(key => role.permissions[key])
    return activePermissions.slice(0, 3).map(permission => (
      <span key={permission} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-secondary-100 text-secondary-800 mr-1">
        {permission.split('.')[0]}
      </span>
    ))
  }

  const toggleExpand = (path) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  const isObject = (val) => val && typeof val === 'object' && !Array.isArray(val)

  const getValueAtPath = (obj, path) => {
    return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj)
  }

  const setValueAtPath = (obj, path, value) => {
    const keys = path.split('.')
    const newObj = { ...obj }
    let current = newObj
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i]
      current[key] = { ...(current[key] || {}) }
      current = current[key]
    }
    current[keys[keys.length - 1]] = value
    return newObj
  }

  const handleLeafToggle = (path, checked) => {
    setRolePermissionsDraft((prev) => setValueAtPath(prev || {}, path, checked))
  }

  const getCheckboxState = (path, node) => {
    if (!isObject(node)) {
      return Boolean(getValueAtPath(rolePermissionsDraft || {}, path))
    }
    
    const children = Object.entries(node).map(([key, value]) => {
      const childPath = path ? `${path}.${key}` : key
      return getCheckboxState(childPath, value)
    })
    
    const checkedCount = children.filter(Boolean).length
    const totalCount = children.length
    
    if (checkedCount === 0) return false
    if (checkedCount === totalCount) return true
    return 'indeterminate'
  }

  const renderTree = (node, pathPrefix = '') => {
    if (!isObject(node)) return null
    const entries = Object.entries(node)
    return (
      <ul className="pl-4 space-y-1">
        {entries.map(([key, value]) => {
          const currentPath = pathPrefix ? `${pathPrefix}.${key}` : key
          const isBranch = isObject(value)
          if (isBranch) {
            const expanded = expandedPaths.has(currentPath)
            const checkboxState = getCheckboxState(currentPath, value)
            return (
              <li key={currentPath}>
                <div className="flex items-center space-x-2">
                  <button type="button" className="text-secondary-600" onClick={() => toggleExpand(currentPath)}>
                    {expanded ? '▾' : '▸'}
                  </button>
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={checkboxState === true}
                    ref={(el) => {
                      if (el) el.indeterminate = checkboxState === 'indeterminate'
                    }}
                    onChange={(e) => {
                      const allChildren = Object.keys(value)
                      const shouldCheck = e.target.checked
                      allChildren.forEach(childKey => {
                        const childPath = `${currentPath}.${childKey}`
                        handleLeafToggle(childPath, shouldCheck)
                      })
                    }}
                  />
                  <label className="text-sm text-secondary-900 font-medium">{key}</label>
                </div>
                {expanded && <div className="mt-1">{renderTree(value, currentPath)}</div>}
              </li>
            )
          }
          const checked = Boolean(getValueAtPath(rolePermissionsDraft || {}, currentPath))
          return (
            <li key={currentPath} className="flex items-center space-x-2">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={checked}
                onChange={(e) => handleLeafToggle(currentPath, e.target.checked)}
              />
              <span className="text-sm text-secondary-800">{key}</span>
            </li>
          )
        })}
      </ul>
    )
  }

  const openPermissionModal = (role) => {
    setSelectedRole(role)
    
    // Initialize draft with current role permissions (convert from flat to nested)
    if (role.permissions) {
      const rolePerms = typeof role.permissions === 'string' ? JSON.parse(role.permissions) : role.permissions
      
      // Convert flat permissions (e.g., "organizations.create": true) to nested structure
      const nestedPerms = {}
      Object.keys(rolePerms).forEach(key => {
        if (rolePerms[key] === true) {
          const parts = key.split('.')
          if (parts.length === 2) {
            const [resource, action] = parts
            if (!nestedPerms[resource]) {
              nestedPerms[resource] = {}
            }
            nestedPerms[resource][action] = true
          }
        }
      })
      setRolePermissionsDraft(nestedPerms)
    } else {
      setRolePermissionsDraft({})
    }
    
    // Expand all top-level resources by default
    const topLevelResources = Object.keys(permissions)
    setExpandedPaths(new Set(topLevelResources))
    setIsPermissionModalOpen(true)
  }

  const closePermissionModal = () => {
    setIsPermissionModalOpen(false)
    setExpandedPaths(new Set())
  }

  const savePermissions = async () => {
    if (!selectedRole) return
    
    try {
      const flatPermissions = {}
      const flattenPermissions = (obj, prefix = '') => {
        Object.entries(obj).forEach(([key, value]) => {
          const path = prefix ? `${prefix}.${key}` : key
          if (typeof value === 'object' && value !== null) {
            flattenPermissions(value, path)
          } else if (value === true) {
            flatPermissions[path] = true
          }
        })
      }
      
      flattenPermissions(rolePermissionsDraft)
      
      console.log('Saving permissions for role:', selectedRole.id, flatPermissions)
      
      await rolesAPI.update(selectedRole.id, {
        permissions: flatPermissions
      })

      console.log('Permissions saved successfully')
      alert('Permissions updated successfully!')
      closePermissionModal()
      // Invalidate and refetch roles data
      queryClient.invalidateQueries(['roles'])
      refetch()
    } catch (error) {
      console.error('Error saving permissions:', error)
      alert('Failed to save permissions. Please try again.')
    }
  }
 
  if (!isControlCenter()) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-secondary-900 mb-4">Access Denied</h2>
        <p className="text-secondary-600">You don't have permission to view this page.</p>
      </div>
    )
  }
 
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Roles</h1>
          {/* <p className="text-secondary-600 mt-1">Manage user roles </p> */}
        </div>
        {/* <div className="flex space-x-2 mt-4 sm:mt-0">
          {hasPermission('roles.create') && (
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Role
            </Button>
          )}
          {hasPermission('system.admin') && (
            <Button variant="outline">
              <Shield className="h-4 w-4 mr-2" />
              Initialize Defaults
            </Button>
          )}
        </div> */}
      </div>
 
      {/* Section Header and Search */}
      <div className="flex items-center justify-between mt-4">
        {/* <div>
          <h2 className="text-xl font-semibold text-secondary-900">Roles</h2>
          <p className="text-secondary-600 text-sm">Manage user roles</p>
        </div> */}
        {hasPermission('roles.create') && (
          <Button onClick={openAddRoleModal}>
            <Plus className="h-4 w-4 mr-2" />
            Add Role
          </Button>
        )}
      </div>
      <Card className="mt-4">
        <CardContent className="p-4">
          <label className="block text-xs font-medium text-secondary-600 mb-2">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
            <Input
              placeholder="Search roles..."
              value={searchTerm}
              onChange={handleSearch}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Roles Table */}
      <Card className="mt-4">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Permissions</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-200">
                  {roles.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-sm text-secondary-500">
                        {searchTerm.trim()
                          ? 'No roles match your search.'
                          : 'No roles found.'}
                      </td>
                    </tr>
                  ) : (
                  roles.map((role) => (
                    <tr key={role.id} className="hover:bg-secondary-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">{role.name}</td>
                      <td className="px-6 py-4 text-sm text-secondary-700">
                        {role.description || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-secondary-700">
                        <div className="max-w-md">
                          <div className="truncate" title={getPermissionList(role)}>
                            {getPermissionList(role)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-700">
                        <div className="flex items-center space-x-2">
                          {hasPermission('roles.update') && (
                            <button
                              onClick={() => {
                                const currentStatus = role.status || 'active'
                                toggleRoleStatusMutation.mutate({
                                  roleId: role.id,
                                  status: currentStatus === 'active' ? 'inactive' : 'active'
                                })
                              }}
                              className="inline-flex items-center focus:outline-none"
                              disabled={toggleRoleStatusMutation.isLoading}
                              title={(role.status || 'active') === 'active' ? 'Disable' : 'Enable'}
                            >
                              {(role.status || 'active') === 'active' ? (
                                <ToggleRight className="h-6 w-6 text-green-600" />
                              ) : (
                                <ToggleLeft className="h-6 w-6 text-gray-400" />
                              )}
                            </button>
                          )}
                          {hasPermission('roles.update') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditRoleModal(role)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          )}
                          {hasPermission('roles.delete') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setRoleToDelete(role)
                                setShowDeleteModal(true)
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {roles.length === 0 && !isLoading && !searchTerm.trim() && (
        <Card>
          <CardContent className="text-center py-12">
            <Shield className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">No roles found</h3>
            <p className="text-secondary-600">Get started by creating your first role.</p>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Role Modal */}
      <AddRoleModal
        isOpen={isAddRoleModalOpen}
        onClose={closeAddRoleModal}
        onSubmit={handleRoleSubmit}
        role={editingRole}
        isSubmitting={isSubmitting || createRoleMutation.isLoading || updateRoleMutation.isLoading}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setRoleToDelete(null)
        }}
        onConfirm={() => {
          if (roleToDelete) {
            deleteRoleMutation.mutate(roleToDelete.id)
          }
        }}
        title="Delete Role"
        message={`Are you sure you want to delete "${roleToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
      />

      {/* Add Permission Modal */}
      {isPermissionModalOpen && selectedRole && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <div className="flex items-center space-x-2">
                {/* <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Plus className="h-4 w-4 text-blue-600" />
                </div> */}
                <CardTitle>Add Permission</CardTitle>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={closePermissionModal}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>

            <CardContent>
              <div className="max-h-96 overflow-y-auto">
                {console.log('Rendering permissions tree with:', permissions)}
                {permissionsLoading ? (
                  <div className="text-center py-8 text-gray-500">
                    <LoadingSpinner size="lg" />
                    <p className="mt-2">Loading permissions...</p>
                  </div>
                ) : Object.keys(permissions).length > 0 ? (
                  renderTree(permissions)
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No permissions available</p>
                    <p className="text-sm mt-2">Debug: {JSON.stringify(permissions)}</p>
                  </div>
                )}
              </div>
              
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-secondary-200 mt-4">
                <Button
                  variant="outline"
                  onClick={closePermissionModal}
                >
                  Cancel
                </Button>
                <Button onClick={savePermissions}>
                  Update
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}